export const COMPLIANCE_FILTERS = Object.freeze({
  ALL: 'all',
  COMPLIANT: 'compliant',
  NON_COMPLIANT: 'non_compliant',
  NEEDS_HUMAN_REVIEW: 'needs_human_review',
  NOT_REVIEWED: 'not_reviewed',
  STALE: 'stale',
});

export const EMPTY_COMPLIANCE_SUMMARY = Object.freeze({
  total: 0,
  compliant: 0,
  nonCompliant: 0,
  needsHumanReview: 0,
  notReviewed: 0,
  stale: 0,
  staleCompliant: 0,
  staleNonCompliant: 0,
  staleNeedsHuman: 0,
});

/**
 * Classify one decision blob into a single summary bucket.
 * Stale wins because it is the action-requiring state.
 */
export const classifyComplianceForSummary = (decision) => {
  if (!decision || !decision.status) return 'notReviewed';
  if (Boolean(decision.is_stale)) return 'stale';
  if (decision.status === COMPLIANCE_FILTERS.NEEDS_HUMAN_REVIEW) return 'needsHumanReview';
  if (decision.status === COMPLIANCE_FILTERS.COMPLIANT) return 'compliant';
  if (decision.status === COMPLIANCE_FILTERS.NON_COMPLIANT) return 'nonCompliant';
  return 'notReviewed';
};

export const matchesComplianceFilter = (decision, filter) => {
  if (filter === COMPLIANCE_FILTERS.ALL) return true;
  if (filter === COMPLIANCE_FILTERS.STALE) {
    return Boolean(decision?.is_stale);
  }
  if (filter === COMPLIANCE_FILTERS.NOT_REVIEWED) {
    return !decision || !decision.status;
  }
  // Stale decisions are a distinct bucket: they should *only* appear
  // under the "Changed since review" filter, not under compliant/flagged.
  if (Boolean(decision?.is_stale)) return false;
  return decision?.status === filter;
};

// ─── Provenance helpers ─────────────────────────────────────────────────
//
// One predicate per automated action (bulk + AI). Both consume the same
// compliance blob shape stored in `contact_field_values` and emitted by
// `useReviewerCampaign`. Consumers (modals + apply hooks) MUST use these
// helpers — never re-implement the rule inline. Drift here means the
// preview lies or, worse, the apply path overwrites a cell the preview
// promised to preserve.
//
// Truth table (for both predicates, see the plan for full matrix):
//
//   not_reviewed (no blob)              → both: act
//   is_stale (any provenance)           → both: act (re-stamp clears amber)
//   per-cell human flag, fresh          → both: skip (manual reviewer wins)
//   bulk-applied human, fresh, same vd  → bulk: skip (true idempotent no-op)
//   bulk-applied human, fresh, diff vd  → bulk: act (reversibility)
//   AI-applied, fresh                   → bulk: ALWAYS act (provenance flip;
//                                                 human is explicitly endorsing
//                                                 or overriding the AI verdict)
//                                       → AI:   skip (already decided; tokens)
//
// Per-cell human flags are detected as `decided_by === 'human'` AND
// `bulk_applied !== true`. The bulk_applied flag is the discriminator
// between "deliberate per-cell human work" (sacred, preserved) and
// "human triggered a bulk action that landed here" (overwritable so
// bulk runs stay reversible).

const isPerCellHumanProtected = (compliance) =>
  compliance.decided_by === 'human' && compliance.bulk_applied !== true;

/**
 * Whether a bulk verdict should act on a given cell. A cell is acted on
 * when applying the verdict would change something the reviewer cares
 * about — chip status flip, staleness overlay clear, OR provenance flip
 * from AI to human-bulk. True no-ops (same human bulk verdict, not
 * stale) are excluded so the preview count matches reality.
 *
 * AI-decided cells are NEVER treated as no-ops here: even when the
 * verdict matches, running a bulk action on an AI cell is a meaningful
 * "human signs off on this" event — provenance flips to
 * `human + bulk_applied=true`, the chip's Sparkles badge swaps to the
 * Human icon, and the cell becomes reversible-by-bulk like any other
 * bulk write.
 */
export const shouldApplyBulk = (compliance, verdict) => {
  if (!compliance) return true;
  if (compliance.is_stale === true) return true;
  if (isPerCellHumanProtected(compliance)) return false;
  if (compliance.decided_by === 'ai') return true;
  if (compliance.status === verdict) return false;
  return true;
};

/**
 * Whether the AI review fanout should act on a given cell. Same shape
 * as the bulk predicate but with two key differences:
 *   - No verdict argument — AI decides the verdict, the predicate only
 *     decides whether AI gets to look at the cell at all.
 *   - AI-already-decided fresh cells are skipped (saves tokens; if the
 *     reviewer wants a fresh AI verdict they can flip the cell first).
 *
 * IMPORTANT: mirrored server-side in `llm/fetch/compliance_review_fetch.py`.
 * Used when eligibility is evaluated against **current** DB state (after
 * any clearing step). For the "Review with AI" modal preview, prefer
 * `shouldRunAiAfterClearPreview`, which matches post-clear eligibility.
 */
export const shouldRunAi = (compliance) => {
  if (!compliance) return true;
  if (compliance.is_stale === true) return true;
  if (isPerCellHumanProtected(compliance)) return false;
  if (compliance.decided_by === 'ai') return false;
  return true;
};

/**
 * Preview predicate for starting an AI compliance **full run**.
 * Matches server behavior after `clear_prior_compliance_for_ai_review_rerun`
 * recomputes eligibility: prior AI / bulk-human verdict rows are wiped, so only
 * sacred per-cell human cells stay skipped. Use this for modal counts enabling
 * re-run — not `shouldRunAi`, which skips fresh AI in cached payload state.
 */
export const shouldRunAiAfterClearPreview = (compliance) => {
  if (!compliance) return true;
  if (compliance.is_stale === true) return true;
  if (isPerCellHumanProtected(compliance)) return false;
  return true;
};

// ─── Preview-count helpers ──────────────────────────────────────────────
//
// Both modals show "Will mark N cells / Skip M" preview strips. These
// helpers walk the contact list once per render and return both counts.
// `configIdSet` is a Set of selected draft `config_id`s.

const walkSelectedCells = (contacts, configIdSet, predicate) => {
  if (!configIdSet || configIdSet.size === 0) {
    return { willAct: 0, willSkip: 0 };
  }
  let willAct = 0;
  let willSkip = 0;
  for (const row of contacts) {
    for (const cfg of configIdSet) {
      const cell = row?.drafts?.[cfg];
      if (!cell) continue;
      if (predicate(cell.compliance)) willAct += 1;
      else willSkip += 1;
    }
  }
  return { willAct, willSkip };
};

export const countBulkScope = (contacts, configIdSet, verdict) => {
  const { willAct, willSkip } = walkSelectedCells(
    contacts || [],
    configIdSet,
    (compliance) => shouldApplyBulk(compliance, verdict),
  );
  return { willUpdate: willAct, willSkip };
};

export const countAiScope = (contacts, configIdSet) => {
  const { willAct, willSkip } = walkSelectedCells(
    contacts || [],
    configIdSet,
    shouldRunAiAfterClearPreview,
  );
  return { willReview: willAct, willSkip };
};
